import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {getLocalizedValue} from "crqlar-react-common/dist/utils/translations.util";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {RightSidebarFloatingPanel} from "../../layout/rightSidebar/RightSidebarFloatingPanel";
import {RightSidebarPanelHeader} from "../../layout/rightSidebar/RightSidebarPanelHeader";
import {RightSidebarPanelButton} from "../../layout/rightSidebar/RightSidebarRail";

type SortDirection = "asc" | "desc";

interface RestaurantOption {
    id: string;
    label: string;
    color?: string;
}

export interface MultipleRestaurantResourcePickerProps {
    restaurants: readonly Resource[];
    selectedRestaurantIds: readonly string[];
    onSelectedRestaurantIdsChange: (restaurantIds: string[]) => void;
}

const selectableRowSx = (selected: boolean) => ({
    m: 0,
    px: 0.75,
    py: 0.5,
    width: "100%",
    minWidth: 0,
    borderRadius: 1,
    bgcolor: selected ? "action.selected" : "transparent",
    "&:hover": {bgcolor: "action.hover"},
    "& .MuiFormControlLabel-label": {flex: 1, minWidth: 0},
});

export function MultipleRestaurantResourcePicker({
    restaurants,
    selectedRestaurantIds,
    onSelectedRestaurantIdsChange,
}: MultipleRestaurantResourcePickerProps) {
    const {t, i18n} = useTranslation();
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
    const [sortAnchorEl, setSortAnchorEl] = useState<HTMLElement | null>(null);
    const locale = i18n.resolvedLanguage || i18n.language;
    const options = useMemo<RestaurantOption[]>(() => {
        const direction = sortDirection === "asc" ? 1 : -1;
        const collator = new Intl.Collator(locale, {numeric: true, sensitivity: "base"});

        return restaurants
            .filter((restaurant): restaurant is Resource & {id: string} => Boolean(restaurant.id))
            .map(restaurant => ({
                id: restaurant.id,
                label: getLocalizedValue(locale, restaurant.nameLocalized) || restaurant.name || t("unnamed"),
                color: restaurant.meta?.tagColor,
            }))
            .sort((left, right) => direction * collator.compare(left.label, right.label));
    }, [locale, restaurants, sortDirection, t]);
    const optionIdSet = useMemo(() => new Set(options.map(option => option.id)), [options]);
    const selectedIdSet = useMemo(
        () => new Set(selectedRestaurantIds.filter(id => optionIdSet.has(id))),
        [optionIdSet, selectedRestaurantIds],
    );
    const allSelected = options.length > 0 && options.every(option => selectedIdSet.has(option.id));

    const toggleRestaurant = (restaurantId: string) => {
        const nextIds = new Set(selectedIdSet);

        if (nextIds.has(restaurantId)) {
            nextIds.delete(restaurantId);
        } else {
            nextIds.add(restaurantId);
        }

        onSelectedRestaurantIdsChange(Array.from(nextIds));
    };

    return (
        <RightSidebarPanelButton
            icon={(
                <Badge
                    badgeContent={selectedIdSet.size}
                    showZero
                >
                    <RestaurantMenuIcon/>
                </Badge>
            )}
            label={t("Restaurants")}
            renderPanel={close => (
                <RightSidebarFloatingPanel sx={{overflow: "hidden"}}>
                    <RightSidebarPanelHeader title={t("Restaurants")} onClose={close}/>

                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                        sx={{px: 2, pb: 1}}
                    >
                        <Button
                            type="button"
                            size="small"
                            color="info"
                            onClick={() => onSelectedRestaurantIdsChange(
                                allSelected ? [] : options.map(option => option.id),
                            )}
                        >
                            {allSelected ? t("Deselect all") : t("Select all")}
                        </Button>
                        <Button
                            type="button"
                            size="small"
                            color="info"
                            endIcon={<ArrowDropDownIcon/>}
                            onClick={event => setSortAnchorEl(event.currentTarget)}
                            sx={{minWidth: 0, whiteSpace: "nowrap"}}
                        >
                            {sortDirection === "asc" ? "A–Z" : "Z–A"}
                        </Button>
                        <Menu
                            anchorEl={sortAnchorEl}
                            open={Boolean(sortAnchorEl)}
                            onClose={() => setSortAnchorEl(null)}
                            anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                            transformOrigin={{vertical: "top", horizontal: "right"}}
                        >
                            <MenuItem
                                selected={sortDirection === "asc"}
                                onClick={() => {
                                    setSortDirection("asc");
                                    setSortAnchorEl(null);
                                }}
                            >
                                A–Z
                            </MenuItem>
                            <MenuItem
                                selected={sortDirection === "desc"}
                                onClick={() => {
                                    setSortDirection("desc");
                                    setSortAnchorEl(null);
                                }}
                            >
                                Z–A
                            </MenuItem>
                        </Menu>
                    </Stack>

                    <Stack sx={{flex: 1, minHeight: 0, overflowY: "auto", px: 2, pb: 2, gap: 0.25}}>
                        {options.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                {t("No restaurants available")}
                            </Typography>
                        ) : options.map(option => {
                            const selected = selectedIdSet.has(option.id);

                            return (
                                <FormControlLabel
                                    key={option.id}
                                    control={(
                                        <Checkbox
                                            checked={selected}
                                            onChange={() => toggleRestaurant(option.id)}
                                            size="small"
                                            sx={{p: 0.5, mr: 0.5}}
                                        />
                                    )}
                                    label={(
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            gap={1}
                                            sx={{width: "100%", minWidth: 0, pr: 1}}
                                        >
                                            <Typography noWrap>{option.label}</Typography>
                                            <Box
                                                aria-hidden
                                                sx={{
                                                    width: 14,
                                                    height: 14,
                                                    flexShrink: 0,
                                                    borderRadius: "50%",
                                                    bgcolor: option.color || "action.disabledBackground",
                                                }}
                                            />
                                        </Stack>
                                    )}
                                    sx={selectableRowSx(selected)}
                                />
                            );
                        })}
                    </Stack>
                </RightSidebarFloatingPanel>
            )}
        />
    );
}

export default MultipleRestaurantResourcePicker;
